/**
 * Safe rewriting of SELECT list to dimN_name / metricN aliases using node-sql-parser AST.
 * Avoids regex pitfalls: SELECT *, AS inside aggregate args, nested parentheses, etc.
 */

function parseAstErrorMessage(e) {
  const m = e && (e.message || e.sqlMessage || String(e));
  return m && String(m).trim() ? String(m).trim() : "SQL parse error";
}

function normalizeExprKey(sql) {
  return String(sql || "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function minSelectAst(columns) {
  return {
    with: null,
    type: "select",
    options: null,
    distinct: null,
    columns,
    into: { position: null },
    from: null,
    where: null,
    groupby: null,
    having: null,
    orderby: null,
    limit: null,
    locking_read: null,
    window: null,
    collate: null,
  };
}

/**
 * SQL text for a single SELECT-list expression (no alias).
 * @param {import('node-sql-parser').Parser} sqlParser
 * @param {object} expr
 * @returns {string}
 */
export function exprToSelectFragmentSql(sqlParser, expr) {
  if (!expr) return "";
  try {
    const colNode = { type: "expr", expr, as: null };
    const s = sqlParser.sqlify(minSelectAst([colNode]), {
      database: "PostgreSQL",
    });
    return s.replace(/^select\s+/i, "").trim();
  } catch {
    return "";
  }
}

/**
 * True if this SELECT-list expression is a bare * (including qualified table.*).
 */
export function isStarSelectExpr(expr) {
  if (!expr) return false;
  if (expr.type === "star") return true;
  if (expr.type === "column_ref") {
    const c = expr.column;
    if (c === "*" || c === "`*`") return true;
    if (Array.isArray(c) && c.length && c[c.length - 1] === "*") return true;
  }
  return false;
}

/**
 * True if expression contains an aggregate or window aggregate anywhere.
 * @param {object} expr
 * @param {number} depth
 * @returns {boolean}
 */
export function exprContainsAggregate(expr, depth = 0) {
  if (!expr || depth > 80) return false;
  if (expr.type === "aggr_func" || expr.type === "window_func") return true;

  if (expr.type === "function") {
    const nm = String(
      expr.name?.name ?? expr.name?.value ?? expr.name ?? "",
    ).toLowerCase();
    const aggs = [
      "sum",
      "avg",
      "count",
      "min",
      "max",
      "every",
      "some",
      "stddev_pop",
      "stddev_samp",
      "var_pop",
      "var_samp",
      "regr_count",
      "regr_sxx",
      "regr_syy",
      "regr_avgx",
      "regr_avgy",
      "regr_slope",
      "regr_intercept",
      "corr",
      "covar_pop",
      "covar_samp",
      "string_agg",
      "array_agg",
      "json_agg",
      "jsonb_agg",
      "xmlagg",
      "mode",
      "percentile_cont",
      "percentile_disc",
      "rank",
      "dense_rank",
      "row_number",
    ];
    if (aggs.includes(nm)) return true;
    if (expr.args?.expr) {
      const inner = expr.args.expr;
      if (Array.isArray(inner)) {
        for (const e of inner) {
          if (exprContainsAggregate(e, depth + 1)) return true;
        }
      } else if (exprContainsAggregate(inner, depth + 1)) return true;
    }
  }

  if (expr.type === "case" && Array.isArray(expr.args)) {
    for (const a of expr.args) {
      if (!a || typeof a !== "object") continue;
      if (exprContainsAggregate(a.expr, depth + 1)) return true;
      if (exprContainsAggregate(a.cond, depth + 1)) return true;
      if (exprContainsAggregate(a.result, depth + 1)) return true;
    }
  }

  if (expr.type !== "function" && expr.args) {
    const inner = expr.args.expr;
    if (Array.isArray(inner)) {
      for (const e of inner) {
        if (exprContainsAggregate(e, depth + 1)) return true;
      }
    } else if (inner && exprContainsAggregate(inner, depth + 1)) return true;
  }

  if (expr.left && exprContainsAggregate(expr.left, depth + 1)) return true;
  if (expr.right && exprContainsAggregate(expr.right, depth + 1)) return true;

  if (expr.expr && (expr.type === "cast" || expr.type === "unary_expr")) {
    return exprContainsAggregate(expr.expr, depth + 1);
  }

  if (expr.type === "select" && Array.isArray(expr.columns)) {
    for (const c of expr.columns) {
      if (c && exprContainsAggregate(c.expr, depth + 1)) return true;
    }
  }

  return false;
}

/**
 * Validates SELECT list for alias rewrite (parse + no bare *).
 * @returns {{ ok: true, ast: object } | { ok: false, error: string, errorKind: string }}
 */
export function validateSelectListForAliasRewrite(sqlParser, sql) {
  const trimmed = (sql || "").trim();
  if (!trimmed) {
    return {
      ok: false,
      error: "SQL query cannot be empty.",
      errorKind: "validation",
    };
  }
  let ast;
  try {
    ast = sqlParser.astify(trimmed, { database: "PostgreSQL" });
  } catch (e) {
    return {
      ok: false,
      error: parseAstErrorMessage(e),
      errorKind: "syntax",
    };
  }
  if (Array.isArray(ast)) ast = ast[0];
  if (!ast || ast.type !== "select" || !Array.isArray(ast.columns)) {
    return {
      ok: false,
      error:
        "Expected a single SELECT (or WITH … SELECT) statement with a column list.",
      errorKind: "syntax",
    };
  }
  for (const col of ast.columns) {
    if (col && isStarSelectExpr(col.expr)) {
      return {
        ok: false,
        error:
          "SELECT * cannot be aliased for preview or chart mapping. List explicit columns (e.g. SELECT col_a, col_b, … FROM …) instead of *.",
        errorKind: "unsupported",
      };
    }
  }
  return { ok: true, ast };
}

/**
 * Auto-assign dimN_name / metricN using AST (aggregates → metric*, else dimension).
 * @returns {{ newSql: string, headerMap: Record<string,string>, error?: string, errorKind?: string }}
 */
export function standardizeSelectAliases(sqlParser, sql) {
  const v = validateSelectListForAliasRewrite(sqlParser, sql);
  if (!v.ok) {
    return {
      newSql: "",
      headerMap: {},
      error: v.error,
      errorKind: v.errorKind,
    };
  }
  const ast = JSON.parse(JSON.stringify(v.ast));
  const colCount = ast.columns.length;
  /** Per-column: treat as measure (metric*) vs dimension (dim*_name). */
  const treatAsMetric = ast.columns.map((col) => exprContainsAggregate(col.expr));
  const anyAggregate = treatAsMetric.some(Boolean);
  /**
   * BI queries often expose pre-aggregated measures as plain columns (no SUM/COUNT in AST).
   * If nothing parsed as an aggregate but the list has 2+ columns, treat the last up to two
   * columns as metrics so bar/line/pie preview and dim/metric readiness still work.
   */
  if (!anyAggregate && colCount >= 2) {
    const nMetric = Math.min(2, colCount - 1);
    for (let i = colCount - nMetric; i < colCount; i++) {
      treatAsMetric[i] = true;
    }
  }

  let dimIndex = 1;
  let metricIndex = 1;
  const headerMap = {};
  const newColumns = ast.columns.map((col, i) => {
    const isAgg = treatAsMetric[i];
    const alias = isAgg ? `metric${metricIndex++}` : `dim${dimIndex++}_name`;
    const fragment = exprToSelectFragmentSql(sqlParser, col.expr);
    if (fragment) {
      headerMap[fragment] = alias;
    }
    return { expr: col.expr, as: alias };
  });
  ast.columns = newColumns;
  let newSql;
  try {
    newSql = sqlParser.sqlify(ast, { database: "PostgreSQL" });
  } catch (e) {
    return {
      newSql: "",
      headerMap: {},
      error: parseAstErrorMessage(e),
      errorKind: "syntax",
    };
  }
  return { newSql, headerMap };
}

/**
 * Apply preview mapping rows (expression → alias) using AST; outer AS only, never inside args.
 * @param {import('node-sql-parser').Parser} sqlParser
 * @param {string} sql
 * @param {Array<{ expression?: string, alias?: string }>} previewMappingRows
 */
export function buildPreviewSelectWithAliases(
  sqlParser,
  sql,
  previewMappingRows,
) {
  const v = validateSelectListForAliasRewrite(sqlParser, sql);
  if (!v.ok) {
    return {
      newSql: "",
      headerMap: {},
      error: v.error,
      errorKind: v.errorKind,
    };
  }
  const ast = JSON.parse(JSON.stringify(v.ast));
  const expressionToAlias = {};
  (previewMappingRows || []).forEach((row) => {
    const ex = row.expression && String(row.expression).trim();
    if (!ex) return;
    expressionToAlias[normalizeExprKey(ex)] = String(row.alias || "").trim();
  });

  const headerMap = {};
  const newColumns = ast.columns.map((col) => {
    const fragment = exprToSelectFragmentSql(sqlParser, col.expr);
    const norm = normalizeExprKey(fragment);
    const alias = expressionToAlias[norm];
    if (alias) {
      const displayKey = fragment.replace(/\s+/g, " ").trim();
      headerMap[displayKey] = alias;
      return { expr: col.expr, as: alias };
    }
    return { expr: col.expr, as: col.as || null };
  });
  ast.columns = newColumns;
  try {
    const newSql = sqlParser.sqlify(ast, { database: "PostgreSQL" });
    return { newSql, headerMap };
  } catch (e) {
    return {
      newSql: "",
      headerMap: {},
      error: parseAstErrorMessage(e),
      errorKind: "syntax",
    };
  }
}

/**
 * Raw error text from fetchData / fetchDataWithParam axios errors (before polish).
 * @param {Error} err
 * @returns {string}
 */
function collectFetchErrorTextCandidates(err) {
  const out = [];
  const push = (value) => {
    const text = String(value || "").trim();
    if (text && !out.includes(text)) out.push(text);
  };
  const data = err && err.response && err.response.data;
  if (typeof data === "string") {
    push(data);
  } else if (data && typeof data === "object") {
    [
      data.message,
      data.details,
      data.error,
      data.detail,
      data.errorMsg,
      data.failedQuery,
    ].forEach(push);
    try {
      push(JSON.stringify(data));
    } catch {
      /* ignore */
    }
  }
  if (err && typeof err.message === "string") {
    push(err.message);
  }
  return out;
}

function extractQueryFromErrorText(raw) {
  if (!raw) return "";
  const m = String(raw).match(/\bQuery was:?\s*([\s\S]*)/i);
  return m && m[1] ? m[1].trim() : "";
}

export function extractRawFetchDataErrorText(err) {
  const data = err && err.response && err.response.data;
  if (typeof data === "string" && data.trim()) {
    return data.trim();
  }
  if (data && typeof data === "object") {
    const parts = [
      data.message,
      data.details,
      data.error,
      data.detail,
      data.errorMsg,
    ]
      .filter((v) => typeof v === "string" && v.trim())
      .map((v) => v.trim());
    if (parts.length) {
      return parts.join(" | ");
    }
  }
  if (err && typeof err.message === "string" && err.message.trim()) {
    return err.message.trim();
  }
  return "";
}

/**
 * SQL executed on the server when the API error includes "Query was: …".
 * @param {Error|string} errOrText
 * @returns {string}
 */
export function extractFailedFetchQueryFromError(errOrText) {
  if (typeof errOrText === "string") {
    return extractQueryFromErrorText(errOrText);
  }
  const data = errOrText && errOrText.response && errOrText.response.data;
  if (data && typeof data === "object" && typeof data.failedQuery === "string") {
    const direct = data.failedQuery.trim();
    if (direct) return direct;
  }
  for (const raw of collectFetchErrorTextCandidates(errOrText)) {
    const query = extractQueryFromErrorText(raw);
    if (query) return query;
  }
  return "";
}

/**
 * Pull a user-facing message from an axios error response body.
 * @param {Error} err
 * @param {string} [fallback]
 * @returns {string}
 */
export function extractApiErrorMessage(err, fallback = "") {
  const raw = extractRawFetchDataErrorText(err);
  if (raw) {
    return polishFetchDataErrorText(raw);
  }
  return fallback;
}

const FETCH_DATA_INTERNAL_COLUMNS = new Set(["client_id", "function_id"]);

const SELECT_STAR_AS_MESSAGE =
  "SELECT * from a table cannot use AS or an alias. List the columns you need, or pick columns from the table instead of using SELECT *.";

function isSelectStarAsAliasError(text) {
  if (!text || typeof text !== "string") return false;
  const lower = text.toLowerCase();
  if (/\bselect\s+\*\s+as\b/i.test(text)) return true;
  if (!/syntax error at or near\s+"?as\b/i.test(lower)) return false;
  return lower.includes("select *") || /sql syntax error near\s+"?as"/i.test(text);
}

/**
 * Light client-side polish when the API still returns raw JDBC text.
 * @param {string} raw
 * @returns {string}
 */
export function polishFetchDataErrorText(raw) {
  if (!raw || typeof raw !== "string") return raw;
  let text = raw.trim();
  if (isSelectStarAsAliasError(text)) {
    return SELECT_STAR_AS_MESSAGE;
  }
  if (text.startsWith("fw_app ")) {
    text = text.slice(7).trim();
  }
  const queryIdx = text.search(/\bQuery was:/i);
  if (queryIdx >= 0) {
    text = text.slice(0, queryIdx).trim();
  }
  text = text.replace(/\s+Position:\s*\d+\s*/gi, " ").trim();
  const errIdx = text.search(/ERROR:/i);
  if (errIdx >= 0) {
    text = text.slice(errIdx + 6).trim();
  }
  text = text.replace(/^query threw sqlexception:?\s*/i, "").trim();
  const firstLine = text.split(/\r?\n/)[0].trim();

  if (isSelectStarAsAliasError(text)) {
    return SELECT_STAR_AS_MESSAGE;
  }

  const colMatch = firstLine.match(/column\s+"([^"]+)"\s+does not exist/i);
  if (colMatch) {
    const col = colMatch[1];
    if (FETCH_DATA_INTERNAL_COLUMNS.has(col.toLowerCase())) {
      return "Mandatory Tenant columns are missing, table cannot be used";
    }
    return `Column "${col}" is not available in the selected data source. Check your SQL or column selection and try again.`;
  }

  return firstLine || raw.trim();
}

/**
 * Map axios / fetch errors to user-facing preview labels.
 * @param {Error} err
 * @returns {{ title: string, message: string, kind: string }}
 */
export function classifyPreviewOrFetchError(err) {
  if (!err) {
    return {
      kind: "unknown",
      title: "Preview",
      message: "Unknown error",
    };
  }
  if (err.code === "ERR_CANCELED" || err.name === "CanceledError") {
    return {
      kind: "cancel",
      title: "Preview",
      message: "Request was cancelled.",
    };
  }
  const status = err.response && err.response.status;
  const msg =
    extractApiErrorMessage(
      err,
      "Request failed while loading preview data.",
    ) || "Request failed while loading preview data.";

  if (!err.response) {
    return {
      kind: "network",
      title: "Connection",
      message: msg,
    };
  }
  if (status === 400) {
    return {
      kind: "data_sql",
      title: "Data SQL / request",
      message: msg,
    };
  }
  if (status === 422) {
    return {
      kind: "data_validation",
      title: "Data validation",
      message: msg,
    };
  }
  if (status >= 500) {
    return {
      kind: "server",
      title: "Server error",
      message: msg,
    };
  }
  return {
    kind: "http",
    title: "Preview data",
    message: msg,
  };
}
